/**
 * FactureFournisseurDetail — page de détail d'une facture fournisseur (iter32 refactor).
 *
 * Présentational uniquement : assemble les sous-composants via le hook useFFActions.
 * Hiérarchie visuelle améliorée :
 *   1. FFSummaryCard      — tuiles métriques (statut, total, reste, échéance)
 *   2. Grid 2 colonnes :
 *      - FFFilesPanel     — pièces jointes + analyse IA (sticky desktop)
 *      - FFFormFields     — formulaire avec sections collapsibles
 *   3. FFRepartitionSection — répartition analytique (V1 + V2 transitoire)
 *   4. FFNotesCreditCard  — notes de crédit (repliable)
 *   5. FFActionBar        — bandeaux contextuels + barre d'actions fixe + dialogs
 */
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { useFFActions } from "@/hooks/useFFActions";
import FFSummaryCard from "@/components/ff/FFSummaryCard";
import FFFilesPanel from "@/components/ff/FFFilesPanel";
import FFFormFields from "@/components/ff/FFFormFields";
import FFNotesCreditCard from "@/components/ff/FFNotesCreditCard";
import FFActionBar from "@/components/ff/FFActionBar";
import FFRepartitionSection from "@/components/FFRepartitionSection";
import { useAuth } from "@/contexts/AuthContext";

export default function FactureFournisseurDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUserRole = user?.role;

  const {
    f, loading, saving, ocrRunning, pageBusy, reload,
    updateField, updateMontant, updateTauxTva,
    handleSave, handleOCR, handleValider, handleRejeter, handleRegenerer,
    handlePayer, handleDevalider, handleRevertExport,
    handleAnnulerDefinitivement,
    handleAddNoteCredit, handleDeleteNoteCredit,
    rotateFile, deleteFile, moveFile, mergeToPdf,
  } = useFFActions(id);

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
      </div>
    );
  }
  if (!f) return null;

  const editable = !["payee", "rejetee"].includes(f.statut);
  const pageEditable = ["brouillon", "a_valider"].includes(f.statut);
  const compteVerrouille = ["payee", "rejetee"].includes(f.statut);

  return (
    <div className="p-3 sm:p-6 pb-48 sm:pb-48 max-w-7xl mx-auto" data-testid="page-ff-detail">
      {/* 1. Summary header riche */}
      <FFSummaryCard
        f={f}
        onBack={() => navigate("/factures-fournisseurs")}
        onOCR={() => handleOCR(!!f.donnees_ocr)}
        ocrRunning={ocrRunning}
        editable={editable}
      />

      {/* 2. Grid 2 colonnes : Fichiers / Formulaire */}
      <div className="grid lg:grid-cols-2 gap-4">
        <FFFilesPanel
          f={f}
          id={id}
          pageEditable={pageEditable}
          pageBusy={pageBusy}
          rotateFile={rotateFile}
          deleteFile={deleteFile}
          moveFile={moveFile}
          mergeToPdf={mergeToPdf}
          onRefresh={reload}
        />
        <FFFormFields
          f={f}
          editable={editable}
          compteVerrouille={compteVerrouille}
          saving={saving}
          onSave={handleSave}
          updateField={updateField}
          updateMontant={updateMontant}
          updateTauxTva={updateTauxTva}
        />
      </div>

      {/* 3. Répartition analytique */}
      {f.statut !== "rejetee" && f.montant_ttc > 0 && (
        <Card className="p-4 mt-4" data-testid="ff-repartition-card">
          <FFRepartitionSection ff={f} onUpdated={reload} />
        </Card>
      )}

      {/* 4. Notes de crédit */}
      <FFNotesCreditCard
        f={f}
        id={id}
        currentUserRole={currentUserRole}
        onAdd={handleAddNoteCredit}
        onDelete={handleDeleteNoteCredit}
      />

      {/* 5. Actions contextuelles + barre fixe */}
      <FFActionBar
        f={f}
        isAdmin={currentUserRole === "admin"}
        onValider={handleValider}
        onRejeter={handleRejeter}
        onRegenerer={handleRegenerer}
        onDevalider={handleDevalider}
        onRevertExport={handleRevertExport}
        onAnnulerDefinitivement={handleAnnulerDefinitivement}
        onPayer={handlePayer}
      />
    </div>
  );
}
